import { useCallback, useMemo, useState } from 'react';
import { Collapsible } from '@radix-ui/react-collapsible';
import {
  ComponentRendering,
  LayoutServiceData,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { EmblaOptionsType } from 'embla-carousel';
import { GetStaticPropsContext } from 'next';

import { icon_chevron_left, icon_chevron_right } from '@gn-audio/icons';
import {
  Button,
  JabraIcon,
  resolveColorProperty,
  useAsync,
  useFallbackTranslation,
} from '@gn-audio/jss-components';
import {
  getAttributesByFamily,
  getFamilies,
  getFamilyPrices,
  getLocaleWithRegion,
  getPricingApiConfig,
} from '@gn-audio/jss-core';

import { mapToStandardPriceModel } from 'api/pricesApi/priceMapper';
import { StandardPriceModel } from 'api/pricesApi/pricesModel';
import { JabraSlider } from 'common/components/JabraSlider/JabraSlider';
import { parseSCLookupValueList } from 'common/siteCoreHelpers';
import { getProductBuyPageUrl } from 'utils/productUtils';

import { CompareTableColumn } from './CompareTableColumn';
import {
  CompareTableSpotRendering,
  CompareTablesSpotProps,
  FamilyWithPriceAndCurrency,
} from './compareTableSpotTypes';

const CompareTablesSpot = ({
  families,
  productColumns,
  iconBackgroundColor,
}: CompareTablesSpotProps) => {
  const [prices, setPrices] = useState<StandardPriceModel[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useFallbackTranslation();
  const [columnHeights, setColumnHeights] = useState<number[]>([]);

  const handleHeightChange = useCallback((index: number, height: number) => {
    setColumnHeights((prev) => {
      const newHeights = [...prev];
      if (newHeights[index] !== height) {
        newHeights[index] = height;
        return newHeights;
      }
      return prev;
    });
  }, []);

  const maxHeight = useMemo(() => Math.max(0, ...columnHeights), [columnHeights]);

  const familyIds = useMemo(() => families.map((family) => family.familyId), [families]);

  const fetchPrices = async () => {
    const { isSFCCPricingOn } = getPricingApiConfig();
    if (isSFCCPricingOn) {
      const data = await getFamilyPrices(familyIds);
      const fetchedPrices = Array.isArray(data) ? data.map(mapToStandardPriceModel) : [];
      setPrices(fetchedPrices);
    }
  };

  useAsync(fetchPrices, [familyIds]);

  const familiesWithPrice = families.map((family) => {
    const productPrice = prices.find((price) => price.familyId === family.familyId);
    const foundProduct = productColumns.find((product) => +product.product.id === family.familyId);
    const productRow = foundProduct?.productRow || [];
    return {
      ...family,
      price: productPrice || null,
      productRow,
      id: family.familyId.toString(),
    } as FamilyWithPriceAndCurrency;
  });

  const sliderSettings: EmblaOptionsType = {
    slidesToScroll: 1,
    containScroll: 'trimSnaps',
    align: 'start',
  };

  const toggleCollapsible = () => {
    setIsExpanded((prev) => !prev);
  };

  const iconBgColor = iconBackgroundColor?.value ?? '#ffffff';
  const iconBackgroundColorHex = resolveColorProperty(iconBgColor)!;

  const columnRowCount = Math.max(...familiesWithPrice.map((family) => family.productRow.length));

  const sliderContainerMaxHeightClass =
    isExpanded || columnRowCount <= 3 ? 'auto' : 'max-h-[1000px]';

  const CustomButton = (
    direction: 'left' | 'right',
    isEnabled: boolean,
    clickHandler: () => void
  ) => {
    const title = direction === 'left' ? t('system.Previous') : t('system.Next');
    const buttonStyles = direction === 'left' ? 'ml-3 xl:ml-0 left-0' : 'mr-3 xl:mr-0 right-0';
    const icon = direction === 'left' ? icon_chevron_left : icon_chevron_right;
    return (
      <div className={sliderContainerMaxHeightClass}>
        <Button
          size="l"
          variant="tertiary"
          className={classNames('mt-20 flex justify-center', buttonStyles)}
          disabled={!isEnabled}
          title={title}
          icon={
            <JabraIcon
              className={classNames(`flex h-6 ${!isEnabled && 'opacity-50'}`)}
              Icon={icon}
            />
          }
          onClick={clickHandler}
        />
      </div>
    );
  };

  return (
    <>
      {familiesWithPrice.length > 0 && (
        <div className="container-fluid max-w-6xl">
          <Collapsible open={isExpanded}>
            <div className={classNames(`mb-6 overflow-hidden`, sliderContainerMaxHeightClass)}>
              <JabraSlider
                mainClassName="w-full flex"
                slideClassName="basis-full grow md:basis-1/2 xl:basis-1/3"
                sliderSettings={sliderSettings}
                customButton={CustomButton}
                showSlideArrowsOnMobile
              >
                {familiesWithPrice.map((familyWithPrice, index) => (
                  <CompareTableColumn
                    key={familyWithPrice.id}
                    iconBackgroundColor={iconBackgroundColorHex}
                    familyWithPrice={familyWithPrice}
                    maxHeight={maxHeight}
                    onHeightChange={(height) => handleHeightChange(index, height)}
                  />
                ))}
              </JabraSlider>
            </div>
          </Collapsible>

          {columnRowCount > 3 && (
            <div className="flex justify-center">
              <Button variant="tertiary" size="m" onClick={toggleCollapsible}>
                {isExpanded ? t('system.ShowLessFeatures') : t('system.ShowMoreFeatures')}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export const getStaticProps = async (
  rendering: ComponentRendering<CompareTableSpotRendering>,
  _: LayoutServiceData,
  context: GetStaticPropsContext
) => {
  try {
    const locale = getLocaleWithRegion(context.locale);
    if (!rendering.fields) {
      throw new Error('No fields found for Compare Tables Spot');
    }
    const { productColumns, iconBackgroundColor } = rendering.fields;

    const lookupProductsList = productColumns.map((field) => ({
      ...field,
      product: parseSCLookupValueList(field.product.value)[0],
    }));

    const productsWithDetails = lookupProductsList.map((lookup) => ({
      ...lookup,
      productRow: lookup.productRow.map(({ headline = null, icon = null, value = null, id }) => ({
        headline,
        icon,
        value,
        id,
      })),
    }));
    const familyIds = lookupProductsList.map((lookup) => +lookup.product.id);
    const families = await getFamilies(familyIds, locale);

    const familyGroupsWithAttributesList = await Promise.all(
      familyIds.map((id) => getAttributesByFamily(String(id), locale))
    );
    const firstGroupWithAttributesFromEachFamily = familyGroupsWithAttributesList.map(
      (familyGroups) => familyGroups[0] || null
    );
    const familiesWithBuyPageUrl = families.map((family, index) => ({
      ...family,
      buyPageUrl: firstGroupWithAttributesFromEachFamily[index]
        ? getProductBuyPageUrl(firstGroupWithAttributesFromEachFamily[index].attributes) || null
        : null,
    }));

    return {
      families: familiesWithBuyPageUrl,
      productColumns: productsWithDetails,
      iconBackgroundColor: iconBackgroundColor,
    };
  } catch (error) {
    console.error(error);
    return {
      families: [],
      productColumns: [],
      iconBackgroundColor: null,
    };
  }
};
export default withDatasourceCheck()(CompareTablesSpot);
